import {
  TQmulusButtonPresentation,
  TQmulusButtonSeverity,
} from './qmulus-button.ts';

// sorting is important, DO NOT CHANGE ORDER WITHOUT CONSULTING!
// buttons will be ordered in this order from left to right
export const semanticButtonVariantAbort = [
  'reset',
  'resetFilters',
  'cancel',
  'close',
  'back',
  'forward',
] as const;
export const semanticButtonVariantDanger = [
  'delete',
] as const;
export const semanticButtonVariantMutation = [
  'edit',
  'saveAsNew',
  'duplicate',
  'save', // update
  'accept',
  'create',
  'upload', // special form of create for files
] as const;
export const semanticButtonVariantStandalone = [
  'add',
  'settings',
  'tableNavigation',
  'refresh',
  'copy',
  'download',
  'moveUp',
  'moveDown',
] as const;
export const semanticButtonVariantProcess = [
  'calculate',
  'revision',
] as const;
export const semanticButtonVariantChart = [
  'fullscreen',
  'optionMenu',
] as const;
export const semanticButtonVariant = [
  ...semanticButtonVariantProcess,
  ...semanticButtonVariantAbort,
  ...semanticButtonVariantDanger,
  ...semanticButtonVariantMutation,
  ...semanticButtonVariantStandalone,
  ...semanticButtonVariantChart,
] as const;

export type TSemanticButtonVariant = typeof semanticButtonVariant[number];

export interface ISemanticButtonVariantRule {
  icon: string,
  severity: TQmulusButtonSeverity,
  /** Used unless the caller sets a presentation */
  presentation: TQmulusButtonPresentation,
  /** Navigation and abort buttons show their icon only when the label is hidden */
  isIconHiddenWhenLabeled?: boolean,
}

export const SEMANTIC_BUTTON_VARIANT_MAP: Record<TSemanticButtonVariant, ISemanticButtonVariantRule> = {
  accept: {
    icon: 'pi pi-check',
    severity: 'primary',
    presentation: 'labeled',
  },
  add: {
    icon: 'pi pi-plus',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  back: {
    icon: 'pi pi-arrow-left',
    severity: 'tertiary',
    presentation: 'labeled',
    isIconHiddenWhenLabeled: true,
  },
  calculate: {
    icon: 'pi pi-calculator',
    severity: 'tertiary',
    presentation: 'iconOnly',
  },
  cancel: {
    icon: 'pi pi-times',
    severity: 'secondary',
    presentation: 'labeled',
    isIconHiddenWhenLabeled: true,
  },
  close: {
    icon: 'pi pi-times',
    severity: 'secondary',
    presentation: 'labeled',
    isIconHiddenWhenLabeled: true,
  },
  copy: {
    icon: 'pi pi-clipboard',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  create: {
    icon: 'pi pi-plus',
    severity: 'primary',
    presentation: 'labeled',
  },
  delete: {
    icon: 'pi pi-trash',
    severity: 'danger',
    presentation: 'labeled',
  },
  download: {
    icon: 'pi pi-download',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  duplicate: {
    icon: 'pi pi-clone',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  edit: {
    icon: 'pi pi-pencil',
    severity: 'primary',
    presentation: 'labeled',
  },
  forward: {
    icon: 'pi pi-arrow-right',
    severity: 'tertiary',
    presentation: 'labeled',
    isIconHiddenWhenLabeled: true,
  },
  fullscreen: {
    icon: 'pi pi-arrow-up-right-and-arrow-down-left-from-center',
    severity: 'tertiary',
    presentation: 'inline',
  },
  moveDown: {
    icon: 'pi pi-arrow-down',
    severity: 'tertiary',
    presentation: 'inline',
  },
  moveUp: {
    icon: 'pi pi-arrow-up',
    severity: 'tertiary',
    presentation: 'inline',
  },
  optionMenu: {
    icon: 'pi pi-ellipsis-v',
    severity: 'tertiary',
    presentation: 'inline',
  },
  refresh: {
    icon: 'pi pi-sync',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  reset: {
    icon: 'pi pi-undo',
    severity: 'tertiary',
    presentation: 'inline',
  },
  resetFilters: {
    icon: 'pi pi-filter-slash',
    severity: 'tertiary',
    presentation: 'iconOnly',
  },
  revision: {
    icon: 'pi pi-history',
    severity: 'tertiary',
    presentation: 'iconOnly',
  },
  save: {
    icon: 'pi pi-save',
    severity: 'primary',
    presentation: 'labeled',
  },
  saveAsNew: {
    icon: 'pi pi-copy',
    severity: 'tertiary',
    presentation: 'labeled',
  },
  settings: {
    icon: 'pi pi-cog',
    severity: 'tertiary',
    presentation: 'iconOnly',
  },
  tableNavigation: {
    icon: 'pi pi-arrow-right',
    severity: 'tertiary',
    presentation: 'inline',
  },
  upload: {
    icon: 'pi pi-upload',
    severity: 'primary',
    presentation: 'labeled',
  },
};
